import { useDeferredValue, useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { LoadingBlock } from '@/components/PageHeader';

import { useRiskLibraryLines, useRiskLibraryTrades, type RiskLibraryLine } from './riskLibraryApi';

/** 같은 줄 — 서버가 묶은 기준(공종 · 작업 · 위험요인 · 대책)과 같다. 공종을 옮겨 다녀도 고른 줄은 남는다. */
const lineKey = (line: RiskLibraryLine) => [line.trade, line.task ?? '', line.hazard, line.measure ?? ''].join('\u0001');

const has = (value: string | null | undefined, word: string) => !!value && value.toLowerCase().includes(word);

/**
 * 표준 목록에서 불러오기(DOC-136 §7 · 사용자 결정 2026-10-28) — 왼쪽 공종(건수), 오른쪽 그 공종의 줄 표.
 * 공종이 「전체」면 두 글자부터 서버가 찾는다. 고른 줄은 [추가] 로 위험성평가 표 끝에 붙는다.
 */
export function RiskLibraryPicker({ onPick, onClose }: { onPick: (lines: RiskLibraryLine[]) => void; onClose: () => void }) {
  const [trade, setTrade] = useState<string | null>(null);
  const [tradeQuery, setTradeQuery] = useState('');
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<Map<string, RiskLibraryLine>>(() => new Map());
  const word = useDeferredValue(text.trim());

  const trades = useRiskLibraryTrades(true);
  const lines = useRiskLibraryLines(trade, word);

  const tradeWord = tradeQuery.trim().toLowerCase();
  const tradeRows = (trades.data ?? []).filter((one) => !tradeWord || one.trade.toLowerCase().includes(tradeWord));
  const total = (trades.data ?? []).reduce((n, one) => n + one.lineCount, 0);

  // 공종 안에서는 받은 줄을 화면이 거른다 — 공종 하나는 수백 줄이라 다시 묻지 않는다.
  const rows = useMemo(() => {
    const all = lines.data ?? [];
    const lower = word.toLowerCase();
    if (!trade || !lower) return all;
    return all.filter((line) => has(line.task, lower) || has(line.hazard, lower) || has(line.measure, lower) || has(line.accidentType, lower));
  }, [lines.data, trade, word]);

  const toggle = (line: RiskLibraryLine, on: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (on) next.set(lineKey(line), line);
      else next.delete(lineKey(line));
      return next;
    });
  const allOn = rows.length > 0 && rows.every((line) => picked.has(lineKey(line)));
  const toggleAll = (on: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev);
      rows.forEach((line) => (on ? next.set(lineKey(line), line) : next.delete(lineKey(line))));
      return next;
    });

  const tradeButton = (key: string, label: string, count: number, on: boolean, select: () => void) => (
    <button
      key={key}
      type="button"
      onClick={select}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
        padding: '6px 8px',
        borderRadius: '6px',
        border: 'none',
        textAlign: 'left',
        fontSize: '13px',
        cursor: 'pointer',
        background: on ? 'var(--accent-bg)' : 'transparent',
        color: on ? 'var(--accent)' : 'var(--text-primary)',
        fontWeight: on ? 600 : 400,
      }}
    >
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{count.toLocaleString()}</span>
    </button>
  );

  return (
    <Modal
      title="표준 위험요인 목록"
      maxWidth="1180px"
      onClose={onClose}
      actions={
        <button type="button" className="btn-primary" disabled={picked.size === 0} onClick={() => onPick([...picked.values()])}>
          추가{picked.size > 0 ? ` ${picked.size}` : ''}
        </button>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '230px minmax(0, 1fr)', gap: '12px', height: '68vh' }}>
        <aside style={{ display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 0, borderRight: '1px solid var(--border)', paddingRight: '10px' }}>
          <input className="input" style={{ width: '100%' }} placeholder="공종" value={tradeQuery} onChange={(e) => setTradeQuery(e.target.value)} aria-label="공종 찾기" />
          <div style={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
            {trades.isPending ? (
              <LoadingBlock />
            ) : trades.isError ? (
              <LoadingBlock />
            ) : (
              <>
                {!tradeWord && tradeButton('', '전체', total, trade === null, () => setTrade(null))}
                {tradeRows.map((one) => tradeButton(one.trade, one.trade, one.lineCount, trade === one.trade, () => setTrade(one.trade)))}
              </>
            )}
          </div>
        </aside>

        <section style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, minHeight: 0 }}>
          <input className="input" style={{ width: '100%' }} placeholder="작업 · 위험요인 · 감소대책" value={text} onChange={(e) => setText(e.target.value)} aria-label="찾기" />
          <div style={{ overflow: 'auto', minHeight: 0, flex: 1, border: '1px solid var(--border)', borderRadius: '8px' }}>
            {!trade && word.length < 2 ? null : lines.isPending ? (
              <LoadingBlock />
            ) : lines.isError ? (
              <LoadingBlock />
            ) : (
              <table className="site-table" style={{ fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '34px' }}>
                      <input type="checkbox" checked={allOn} onChange={(e) => toggleAll(e.target.checked)} aria-label="모두 고르기" />
                    </th>
                    {!trade && <th style={{ width: '110px' }}>공종</th>}
                    <th style={{ width: '16%' }}>작업</th>
                    <th style={{ textAlign: 'left' }}>위험요인</th>
                    <th style={{ textAlign: 'left' }}>감소대책</th>
                    <th style={{ width: '110px' }}>재해형태</th>
                    <th style={{ width: '44px' }}>등급</th>
                    <th style={{ width: '52px' }}>사용</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((line) => {
                    const key = lineKey(line);
                    const on = picked.has(key);
                    return (
                      <tr key={key} onClick={() => toggle(line, !on)} style={{ cursor: 'pointer', background: on ? 'var(--accent-bg)' : undefined }}>
                        <td>
                          <input type="checkbox" checked={on} onClick={(e) => e.stopPropagation()} onChange={(e) => toggle(line, e.target.checked)} aria-label="고르기" />
                        </td>
                        {!trade && <td>{line.trade}</td>}
                        <td style={{ textAlign: 'left' }}>{line.task ?? ''}</td>
                        <td style={{ textAlign: 'left' }}>{line.hazard}</td>
                        <td style={{ textAlign: 'left' }}>{line.measure ?? ''}</td>
                        <td>{line.accidentType?.replace(/\s*\(.*\)$/, '') ?? ''}</td>
                        <td>{line.grade ?? ''}</td>
                        <td title={line.lastUsed ?? undefined}>{line.usedCount.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
